import {z} from 'zod';

import {isNil, isNotNil} from 'ramda';

import {browserStorageKey} from '@dms/config';

import {TelephonyCallPairableRecordSchema} from '../types/TelephonyCallPairableRecord';

/**
 * Holds customer data, so it lives only alongside a sign-in: logout clears all storage, and a
 * sign-out that keeps it (a lost token) is caught by `isSignedIn` on the next read.
 */
const STORAGE_KEY = 'dms.telephony.pairableCall';

const PairableCallStateSchema = z.object({
  userId: z.string(),
  callId: z.string(),
  isDismissed: z.boolean(),
  /** Newest first. */
  records: z.array(TelephonyCallPairableRecordSchema),
});

export type PairableCallState = z.infer<typeof PairableCallStateSchema>;

type PairableRecord = PairableCallState['records'][number];

/** The refresh token is what every sign-out of the app removes. */
const isSignedIn = () => isNotNil(window.localStorage.getItem(browserStorageKey.REFRESH_TOKEN));

const readStoredState = () => {
  try {
    if (!isSignedIn()) {
      // Signed out since it was written: drop the customer data instead of keeping it around.
      window.localStorage.removeItem(STORAGE_KEY);

      return null;
    }

    const parsed = PairableCallStateSchema.safeParse(
      JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? 'null')
    );

    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
};

const writeStoredState = (state: PairableCallState | null) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage blocked or full: the state still lives for this page load.
  }
};

/**
 * The viewer's last call and the pairable records opened since it started, kept until the viewer
 * dismisses it, the next call starts or the viewer signs out. Shared by every tab of the browser
 * (local storage), so a record opened in one tab and Done in another reach them all; a reload
 * keeps it.
 */
export function createPairableCallStore() {
  let state: PairableCallState | null = readStoredState();
  const listeners = new Set<VoidFunction>();

  const setState = (next: PairableCallState | null) => {
    state = next;
    writeStoredState(next);
    listeners.forEach((listener) => listener());
  };

  // Another tab wrote the call, or signed out (the refresh token went): take the stored state as
  // is, without writing it back; reading it after a sign-out removes it.
  const handleStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY && event.key !== browserStorageKey.REFRESH_TOKEN) {
      return;
    }

    state = readStoredState();
    listeners.forEach((listener) => listener());
  };

  return {
    subscribe: (listener: VoidFunction) => {
      if (listeners.size === 0) {
        window.addEventListener('storage', handleStorage);
      }
      listeners.add(listener);

      return () => {
        listeners.delete(listener);

        if (listeners.size === 0) {
          window.removeEventListener('storage', handleStorage);
        }
      };
    },
    getState: () => state,
    /** A new call replaces the previous one with everything opened for it. */
    rememberCall: (userId: string, callId: string) => {
      if (state?.userId === userId && state.callId === callId) {
        return;
      }

      setState({userId, callId, isDismissed: false, records: []});
    },
    addRecord: (callId: string, record: PairableRecord) => {
      if (isNil(state) || state.callId !== callId || state.isDismissed) {
        return;
      }

      const isSameRecord = (stored: PairableRecord) =>
        stored.resourceId === record.resourceId && stored.recordId === record.recordId;
      const existing = state.records.find(isSameRecord);

      // A record seen before keeps its place and first opening time; only its label refreshes
      // (the page finished loading, or its data changed).
      setState({
        ...state,
        records: isNil(existing)
          ? [record, ...state.records]
          : state.records.map((stored) =>
              isSameRecord(stored) ? {...record, openedAt: stored.openedAt} : stored
            ),
      });
    },
    /** Forgets the call and its records, e.g. once another user signed in to the tab. */
    clear: () => setState(null),
    dismiss: (callId: string) => {
      if (isNil(state) || state.callId !== callId) {
        return;
      }

      setState({...state, isDismissed: true, records: []});
    },
  };
}
